import type { EnhancedDeal, TimberProject } from '../types';
import type { LinkCandidate } from '../components/execInbox/ExecThreadPanel';

export type PushDestinationKind = 'lead_document' | 'project_file' | 'ops_comment';

export interface ResolvedPushTargets {
  leadId?: string;
  projectId?: string;
  dealId?: string;
  leadName?: string;
  projectName?: string;
  defaultKind: PushDestinationKind;
  canLeadDocument: boolean;
  canProjectFile: boolean;
}

function projectForDeal(dealId: string, timberProjects: TimberProject[]): TimberProject | undefined {
  return timberProjects.find((p) => p.dealId === dealId || p.id === dealId);
}

function dealForLead(leadId: string, deals: EnhancedDeal[]): EnhancedDeal | undefined {
  return deals.find((d) => d.leadId === leadId || d.id === leadId);
}

/** Map a CRM link (or explicit candidate) to push targets for Lead Docs / Project Files. */
export function resolvePushTargets(
  linkedTo: LinkCandidate | null | undefined,
  deals: EnhancedDeal[],
  timberProjects: TimberProject[],
): ResolvedPushTargets {
  const empty: ResolvedPushTargets = {
    defaultKind: 'lead_document',
    canLeadDocument: false,
    canProjectFile: false,
  };
  if (!linkedTo) return empty;

  if (linkedTo.type === 'lead') {
    const deal = dealForLead(linkedTo.id, deals);
    const project = deal?.id ? projectForDeal(deal.id, timberProjects) : undefined;
    return {
      leadId: linkedTo.id,
      dealId: deal?.id,
      projectId: project?.id,
      leadName: linkedTo.name,
      projectName: project?.name,
      defaultKind: 'lead_document',
      canLeadDocument: true,
      canProjectFile: !!project?.id,
    };
  }

  if (linkedTo.type === 'deal') {
    const deal = deals.find((d) => d.id === linkedTo.id);
    const project = projectForDeal(linkedTo.id, timberProjects);
    return {
      leadId: deal?.leadId,
      dealId: linkedTo.id,
      projectId: project?.id,
      leadName: linkedTo.name,
      projectName: project?.name || linkedTo.name,
      defaultKind: project?.id ? 'project_file' : 'lead_document',
      canLeadDocument: !!deal?.leadId,
      canProjectFile: !!project?.id,
    };
  }

  if (linkedTo.type === 'project') {
    const project = timberProjects.find((p) => p.id === linkedTo.id);
    const deal = project ? deals.find((d) => d.id === project.dealId) : undefined;
    return {
      leadId: deal?.leadId,
      dealId: project?.dealId,
      projectId: linkedTo.id,
      leadName: deal?.contactName || deal?.title,
      projectName: linkedTo.name,
      defaultKind: 'project_file',
      canLeadDocument: !!deal?.leadId,
      canProjectFile: true,
    };
  }

  return empty;
}

/** Resolve push targets when the user picks a different entity in the override picker. */
export function resolvePushTargetsFromCandidate(
  candidate: LinkCandidate,
  deals: EnhancedDeal[],
  timberProjects: TimberProject[],
): ResolvedPushTargets {
  return resolvePushTargets(candidate, deals, timberProjects);
}
